import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Grid2 as Grid,
  Paper,
  Typography,
  Box,
  Button,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
} from '@mui/material';
import {
  CheckCircle,
  RadioButtonUnchecked,
  ShoppingBag,
  Api,
  VpnKey,
  Storefront,
  Rocket,
  ContentCopy,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import shopService from '../../services/shop.service';
import api from '../../services/api';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const DashboardEntidade: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [copied, setCopied] = useState('');
  const [hasProducts, setHasProducts] = useState(false);
  const [hasApiKey, setHasApiKey] = useState(false);
  const [hasIntegration, setHasIntegration] = useState(false);

  useEffect(() => {
    shopService.getProducts({ limit: 1 }).then(res => {
      const total = res?.pagination?.total ?? res?.data?.length ?? 0;
      setHasProducts(total > 0);
    }).catch(() => {});

    api.get('/admin/api-keys?limit=1').then(res => {
      const keys = res.data?.data ?? res.data ?? [];
      setHasApiKey(Array.isArray(keys) ? keys.length > 0 : keys?.total > 0);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const eid = user?.entityId;
    if (!eid) return;
    api.get(`/admin/entities/${eid}/integration`).then(res => {
      const data = res.data?.data ?? [];
      setHasIntegration(Array.isArray(data) && data.length > 0);
    }).catch(() => {});
  }, [user?.entityId]);

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(label);
      setTimeout(() => setCopied(''), 2000);
    });
  };

  const entityId = user?.entityId || '';

  const gettingStartedSteps = [
    {
      label: 'Conta criada',
      done: true,
      description: 'A sua conta de developer está activa.',
    },
    {
      label: 'Configurar loja',
      done: hasProducts,
      description: hasProducts
        ? 'A sua loja está configurada com produtos.'
        : 'Adicione produtos à sua loja no Pagali Shop.',
      action: () => navigate('/pagalishop'),
      actionLabel: 'Ir para Pagali Shop',
    },
    {
      label: 'Obter API Key',
      done: hasApiKey,
      description: hasApiKey
        ? 'Tem uma chave de API activa para integração.'
        : 'Gere uma chave de API para integração.',
      action: () => navigate('/config/api-keys'),
      actionLabel: 'Gerir API Keys',
    },
    {
      label: 'Integrar checkout',
      done: hasIntegration,
      description: hasIntegration
        ? 'A sua entidade de pagamento está activa.'
        : 'Consulte a documentação para integrar o pagamento.',
      action: () => navigate('/config/api'),
      actionLabel: 'Ver documentação API',
    },
  ];

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        {t('dashboard.welcomeBack')} {user?.name || user?.username}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        {t('dashboard.entitySubtitle')}
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Account Info Card */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Storefront color="primary" />
              <Typography variant="h6" fontWeight={600}>
                A sua conta
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">Username</Typography>
                <Typography variant="body2" fontWeight={500}>{user?.username}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">Email</Typography>
                <Typography variant="body2" fontWeight={500}>{user?.email}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">Perfil</Typography>
                <Chip label={user?.role === 'PFDEV' ? 'Developer' : 'Entidade'} size="small" color="primary" variant="outlined" />
              </Box>
              {entityId && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Entity ID</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" fontWeight={500} sx={{ fontFamily: 'monospace', fontSize: 11 }}>
                      {entityId.substring(0, 16)}...
                    </Typography>
                    <Button
                      size="small"
                      sx={{ minWidth: 'auto', p: 0.5 }}
                      onClick={() => copyToClipboard(entityId, 'entityId')}
                    >
                      <ContentCopy sx={{ fontSize: 14 }} />
                    </Button>
                    {copied === 'entityId' && <Chip label="Copiado!" size="small" color="success" sx={{ height: 20 }} />}
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Quick Links Card */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Rocket color="primary" />
              <Typography variant="h6" fontWeight={600}>
                Acesso rápido
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Button
                variant="outlined"
                startIcon={<ShoppingBag />}
                fullWidth
                sx={{ justifyContent: 'flex-start' }}
                onClick={() => navigate('/pagalishop')}
              >
                Pagali Shop — Gerir produtos e encomendas
              </Button>
              <Button
                variant="outlined"
                startIcon={<Api />}
                fullWidth
                sx={{ justifyContent: 'flex-start' }}
                onClick={() => navigate('/config/api')}
              >
                Documentação API
              </Button>
              <Button
                variant="outlined"
                startIcon={<VpnKey />}
                fullWidth
                sx={{ justifyContent: 'flex-start' }}
                onClick={() => navigate('/config/api-keys')}
              >
                API Keys
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Getting Started */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Primeiros passos
        </Typography>
        <List>
          {gettingStartedSteps.map((step, idx) => (
            <ListItem
              key={idx}
              sx={{ px: 0 }}
              secondaryAction={
                step.action && !step.done ? (
                  <Button size="small" variant="text" onClick={step.action}>
                    {step.actionLabel}
                  </Button>
                ) : null
              }
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                {step.done ? (
                  <CheckCircle color="success" />
                ) : (
                  <RadioButtonUnchecked color="disabled" />
                )}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body1" fontWeight={step.done ? 400 : 500} sx={{ textDecoration: step.done ? 'line-through' : 'none', color: step.done ? 'text.secondary' : 'text.primary' }}>
                    {step.label}
                  </Typography>
                }
                secondary={step.description}
              />
            </ListItem>
          ))}
        </List>
      </Paper>

      {/* API Info */}
      <Alert severity="info" sx={{ mb: 2 }}>
        <strong>Sandbox Base URL:</strong>{' '}
        <code style={{ fontSize: 12 }}>{API_BASE_URL}</code>
        <Button
          size="small"
          sx={{ ml: 1, minWidth: 'auto', p: 0.5 }}
          onClick={() => copyToClipboard(API_BASE_URL, 'apiUrl')}
        >
          <ContentCopy sx={{ fontSize: 14 }} />
        </Button>
        {copied === 'apiUrl' && <Chip label="Copiado!" size="small" color="success" sx={{ height: 20, ml: 1 }} />}
      </Alert>
    </Box>
  );
};

export default DashboardEntidade;
